import React, { useEffect, useRef, useState } from 'react';

// Pulled out of UserManagement.jsx so the Nav Queue tab can use the same picker.
// It could not simply be exported from there: UserManagement imports NavQueue,
// so NavQueue importing UserManagement would be a cycle. Behaviour is unchanged,
// this is a move.

// Shapes the `/mw/employees` response into the option list the picker expects.
// Danish collation, because the names are Danish and a default sort puts
// AA after AB.
export function buildEmployeeOptions(employees) {
  return Object.entries(employees || {})
    .map(([email, emp]) => ({ email, id: emp.id, name: emp.name || email }))
    .sort((a, b) => a.name.localeCompare(b.name, 'da', { sensitivity: 'base' }));
}

export default function EmployeePicker({ id, label, placeholder, options, value, onChange, excludeEmail, disabled, allowSelectAll, selectAllLabel, multi }) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectable = excludeEmail ? options.filter(o => o.email !== excludeEmail) : options;
  const q = query.trim().toLowerCase();
  const filtered = q
    ? selectable.filter(o => o.name.toLowerCase().includes(q) || o.email.toLowerCase().includes(q))
    : selectable;

  const selectedEmails = multi ? value : [];
  const singleSelected = multi ? null : options.find(o => o.email === value);
  const allSelectableSelected = multi && selectable.length > 0 && selectable.every(o => selectedEmails.includes(o.email));
  const someSelectableSelected = multi && selectable.some(o => selectedEmails.includes(o.email));

  const displayValue = multi
    ? (selectedEmails.length === 0 ? '' : selectedEmails.length === 1
        ? (options.find(o => o.email === selectedEmails[0])?.name || selectedEmails[0])
        : `${selectedEmails.length} employees selected`)
    : (singleSelected ? singleSelected.name : '');

  const displayHint = multi
    ? (selectedEmails.length === 1 ? (options.find(o => o.email === selectedEmails[0])?.email || '') : '')
    : (singleSelected ? singleSelected.email : '');

  const selectSingle = (option) => {
    onChange(option.email);
    setOpen(false);
    setQuery('');
  };

  const toggleMulti = (option) => {
    const next = selectedEmails.includes(option.email)
      ? selectedEmails.filter(e => e !== option.email)
      : [...selectedEmails, option.email];
    onChange(next);
  };

  const handleOptionClick = (option) => {
    if (multi) toggleMulti(option);
    else selectSingle(option);
  };

  const handleSelectAllClick = () => {
    onChange(allSelectableSelected ? [] : selectable.map(o => o.email));
  };

  return (
    <div className="combobox" ref={containerRef}>
      {label && <label className="field-label" htmlFor={id}>{label}</label>}
      <div className="combobox-control">
        <input
          id={id}
          type="text"
          className="combobox-input"
          placeholder={placeholder}
          value={open ? query : displayValue}
          onChange={e => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => { setOpen(true); setQuery(''); }}
          onKeyDown={e => {
            if (e.key === 'Escape') setOpen(false);
            else if (e.key === 'Enter' && filtered.length === 1) {
              e.preventDefault();
              if (multi) { toggleMulti(filtered[0]); setQuery(''); }
              else selectSingle(filtered[0]);
            }
          }}
          disabled={disabled}
          autoComplete="off"
        />
        {!open && displayHint && <span className="combobox-email">{displayHint}</span>}
      </div>
      {open && (
        <ul className="combobox-list">
          {allowSelectAll && (
            <li
              className={`combobox-option combobox-option--all${allSelectableSelected ? ' combobox-option--selected' : ''}`}
              onClick={handleSelectAllClick}
            >
              <input
                type="checkbox"
                className="combobox-option-checkbox"
                checked={allSelectableSelected}
                ref={el => { if (el) el.indeterminate = !allSelectableSelected && someSelectableSelected; }}
                onChange={() => {}}
                tabIndex={-1}
              />
              <span className="combobox-option-name">
                {selectAllLabel || 'Select all employees'}
              </span>
              <span className="combobox-option-email">{selectable.length}</span>
            </li>
          )}
          {filtered.length === 0 ? (
            <li className="combobox-empty">No matches</li>
          ) : filtered.slice(0, 50).map(o => {
            const isSelected = multi ? selectedEmails.includes(o.email) : o.email === value;
            return (
              <li
                key={o.email}
                className={`combobox-option${isSelected ? ' combobox-option--selected' : ''}`}
                onClick={() => handleOptionClick(o)}
              >
                {multi && (
                  <input
                    type="checkbox"
                    className="combobox-option-checkbox"
                    checked={isSelected}
                    onChange={() => {}}
                    tabIndex={-1}
                  />
                )}
                <span className="combobox-option-name">{o.name}</span>
                <span className="combobox-option-email">{o.email}</span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
